import { getMountedApps } from 'single-spa';

/**
 * Gets the currently selected language value.
 * @param {string[]} locales The languages available to the current app.
 * @returns {string} The selected language value.
 */
const _getSelectedLanguageValue = (locales) => {
    const selectedLanguageKey = localStorage.getItem('NG_TRANSLATE_LANG_KEY') || 'en-US';
    const findLocale = locales.find((locale) => locale.key === selectedLanguageKey);
    if (!findLocale) {
        localStorage.setItem('NG_TRANSLATE_LANG_KEY', 'en-US');
        return 'English'
    }

    return findLocale.text
}

/**
 * Creates the div that will wrap the button and other child elements that make up the language select menu.
 * @returns {HTMLDivElement} The lanaguage select div.
 */
const _createLanguageSelectDiv = () => {
    const languageSelect = document.createElement('div');
    languageSelect.setAttribute('id', 'language-select-menu');
    languageSelect.classList.add('c-topbar-nav__dropdown');
    languageSelect.style = 'margin-right: 0; margin-left: auto';
    return languageSelect;
};

/**
 * Creates the button displayed on the top bar.
 * @param {string} selectedLanguage The selected language to display.
 * @returns {HTMLButtonElement} The button.
 */
const _createLanguageSelectButton = (selectedLanguage) => {
    const languageSelectButton = document.createElement('button');
    languageSelectButton.setAttribute('id', 'language-select-button');
    languageSelectButton.setAttribute('aria-haspopup', 'true');
    languageSelectButton.setAttribute('aria-expanded', 'false');
    languageSelectButton.setAttribute('type', 'button');
    languageSelectButton.classList.add(
        'c-dropdown',
        'c-topbar-nav__dropdown-button'
    );

    const buttonIcon = document.createElement('i');
    buttonIcon.classList.add('wx-icon', 'wx-icon-earth');

    const nonBreakingSpace = document.createTextNode('\u00A0');

    const buttonText = document.createElement('div');
    buttonText.setAttribute('id', 'language-select-button-text');
    buttonText.innerText = selectedLanguage;

    languageSelectButton.appendChild(buttonIcon);
    languageSelectButton.appendChild(nonBreakingSpace);
    languageSelectButton.appendChild(buttonText);
    return languageSelectButton;
};

/**
 * Creates the list of languages to display when the language select button is pressed.
 * @param {string[]} locales The list of languages that the user can select.
 * @returns {HTMLUListElement} The list of languages to display.
 */
const _createLanguageSelectList = (locales) => {
    const languageSelectList = document.createElement('ul');
    languageSelectList.setAttribute('id', 'language-select-list');
    languageSelectList.setAttribute('role', 'menu');
    languageSelectList.classList.add(
        'c-dropdown__menu',
        'c-dropdown__menu--large',
        'c-dropdown__menu--simple-transition'
    );

    const languageSelectOptions = locales.map((locale) => {
        const listItem = document.createElement('li');
        listItem.setAttribute('role', 'presentation');
        listItem.classList.add('c-dropdown__item');

        const itemLink = document.createElement('a');
        itemLink.setAttribute('role', 'menuItem');
        itemLink.classList.add('dropdown-item', 'c-dropdown__item-link');
        itemLink.innerText = locale.text;

        listItem.onclick = () => handleLanguageChange(locale.key);
        listItem.appendChild(itemLink);
        return listItem;
    });

    languageSelectList.append(...languageSelectOptions);
    return languageSelectList;
};

/**
 * Validates a list of locales, outputting a warning to the console for any invalid entries found.
 * @param {string[]} locales The locales to validate.
 * @returns {string[]} A list of the valid locales.
 */
const _validateLocales = (locales) =>
    locales.filter((locale) => {
        if (
            !locale.key ||
            locale.key.length < 1 ||
            !locale.text ||
            locale.text.length < 1
        ) {
            console.warn('Found invalid locale', locale);
            return false;
        }
        return true;
    });

/**
 * Handles when a language is changed.
 * @param {string} localeKey The selected locale key.
 */
const handleLanguageChange = (localeKey) => {
    const mountedApps = getMountedApps();

    // When on a mounted micro app page a reload is required in order to have the change take effect
    let reloadRequired = mountedApps.some((mounted) => ['inspections', '@weever/flexDSM', '@weever/formFolders', '@weever/opsDashboard'].includes(mounted))
    if (reloadRequired) {
        localStorage.setItem('NG_TRANSLATE_LANG_KEY', localeKey);
        window.location.reload(true);
    }

    window.$wx.mfeLanguageChangedEvent.data = localeKey;
    window.dispatchEvent(window.$wx.mfeLanguageChangedEvent);
};

/**
 * Renders the language select menu.
 * @param {string[] | undefined} locales The locales available to the app.
 * @returns void.
 */
const renderLanguageSelectMenu = (locales) => {
    if (!locales || locales.length <= 1) {
        return;
    }
    locales = _validateLocales(locales);
    const selectedLanguageValue = _getSelectedLanguageValue(locales);

    const languageSelect = _createLanguageSelectDiv();
    const languageSelectButton = _createLanguageSelectButton(
        selectedLanguageValue
    );
    const languageSelectList = _createLanguageSelectList(locales);

    const spacer = document
        .getElementsByClassName('c-topbar__spacer--right')
        ?.item(0);

    languageSelectButton.appendChild(languageSelectList);
    languageSelect.appendChild(languageSelectButton);
    spacer?.parentNode.insertBefore(languageSelect, spacer.nextSibling);

    languageSelectButton.addEventListener('click', () => {
        languageSelectList.classList.toggle('show');
    });

    window.addEventListener('click', (event) => {
        if (!languageSelectButton.contains(event.target)) {
            languageSelectList.classList.toggle('show', false);
        }
    });
};

export default renderLanguageSelectMenu;
